import { getAgeDescription } from "./age";
import { getGlassesDescription } from "./glasses";
import { getHairDescription } from "./hair";
import { removeExtraSpaces } from "../removeExtraSpaces";
import { NewModifiersStructure } from "../processPromptTriggers";
import { ModelAttributesCore } from "../model/attributes";

const getFacialHairDescription = (
  gender: string,
  facialHairColor?: string,
  facialHairStyle?: string,
  hairColor?: string
): string => {
  if (gender !== "Male" || !facialHairStyle) return "";

  if (facialHairStyle === "None" || facialHairStyle === "Clean Shaven") {
    return "";
  }

  // Optimize prompt: only include facial hair color if it's different from hair color
  const shouldIncludeColor =
    facialHairColor &&
    facialHairColor.toLowerCase() !== hairColor?.toLowerCase();

  // Format: "and a [color] [style]" - e.g., "and a short beard", "and a gray mustache"
  const colorPart = shouldIncludeColor
    ? `${facialHairColor.toLowerCase()} `
    : "";
  return `and a ${colorPart}${facialHairStyle.toLowerCase()}`;
};

export const generateModelDescription = (
  attributes: PrismaJson.ModelAttributes | ModelAttributesCore,
  {}: {
    enabledAge?: boolean;
    modifiers?: NewModifiersStructure;
  }
): string => {
  // Early return if no attributes
  if (!attributes) return "TOK";

  // Extract and process basic attributes
  const gender = attributes.gender === "Male" ? "man" : "woman";
  const age = getAgeDescription(attributes.age || "");
  const glasses = getGlassesDescription(attributes.glasses || false);

  // Build base description: "[age] TOK [gender]"
  const baseParts = [
    ...(age ? [age] : []),
    attributes.gender ? `TOK ${gender}` : "TOK",
  ];

  // Collect descriptors (everything except skin tone)
  const descriptors = [
    ...buildEyeDescriptors(attributes),
    ...buildGlassesDescriptors(glasses),
    ...buildHairAndFacialHairDescriptors(attributes),
  ].filter(Boolean);

  // Assemble final description
  return assembleDescription(
    baseParts.join(" "),
    descriptors,
    attributes.skinTone
  );
};

// Helper functions for better organization and reusability
function buildEyeDescriptors(
  attributes: PrismaJson.ModelAttributes | ModelAttributesCore
): string[] {
  return attributes.eyeColor ? [`with ${attributes.eyeColor} eyes`] : [];
}

function buildGlassesDescriptors(glasses: string): string[] {
  return glasses ? ["wearing glasses"] : [];
}

function buildHairAndFacialHairDescriptors(
  attributes: PrismaJson.ModelAttributes | ModelAttributesCore
): string[] {
  const hair = getHairDescription(
    attributes.hair ?? true, // Default to true if undefined (legacy compatibility)
    attributes.hairColor || "",
    "", // hairLength deprecated
    "",
    attributes.hairStyle || ""
  );

  const facialHair =
    getFacialHairDescription(
      attributes?.gender || "",
      attributes?.facialHairColor || "",
      attributes?.facialHairStyle || "",
      attributes?.hairColor || ""
    ) || "";

  // No hair at all
  if (!hair?.trim()) {
    return facialHair?.trim() ? [facialHair.replace("and a", "with a")] : [];
  }

  const hairStyle = attributes.hairStyle;
  const hairColor = attributes.hairColor;
  const isBald = hairStyle?.toLowerCase() === "bald";
  const hasHairstyle = hairStyle && hairStyle !== "My Hairstyle";

  let hairDescription = "";

  if (isBald) {
    hairDescription = "with a bald head";
  } else if (hasHairstyle) {
    const hairParts = [...(hairColor ? [hairColor] : []), hairStyle];
    hairDescription = `with ${hairParts.join(" ")}`;
  } else if (hairColor) {
    hairDescription = `with ${hairColor} hair`;
  }

  // Attach facial hair without comma
  if (facialHair?.trim()) {
    hairDescription += ` ${facialHair}`;
  }

  return hairDescription ? [hairDescription] : [];
}

function assembleDescription(
  base: string,
  descriptors: string[],
  skinTone?: string
): string {
  let description = base;

  if (descriptors.length > 0 && skinTone) {
    description += ` ${descriptors.join(", ")}, and ${skinTone} skin tone`;
  } else if (descriptors.length > 0) {
    description += ` ${descriptors.join(", ")}`;
  } else if (skinTone) {
    description += `, and ${skinTone} skin tone`;
  }

  return removeExtraSpaces(description);
}

// 24 year old TOK man with Brown eyes, Black Short Wavy hair, and Medium skin tone
// 24 year old TOK man with Brown eyes, Black hair, and Medium skin tone
// TOK man with Brown eyes, and Medium skin tone
// 24 year old TOK man, and Medium skin tone
// 24 year old TOK man with Black Short Wavy hair
// {age} year old {gender} with {eyeColor} eyes, {hairColor} {hairStyle}, and {skinTone} skin tone
